<template>
  <modal
    v-if="show"
    height="720px"
    width="1200px"
    bodyPadding="0"
    :title="t('LP Pixel Generator')"
    @close="close"
    :read-article-url="`https://docs.bemob.com/${locale}/no-redirect-tracking`"
  >
    <div style="max-height: 600px;" class="lp-pixel-dialog dialog-wrapper">
      <div class="details lp-pixel-dialog__details-panel">
        <div class="campaign-details__title">
          <div class="subtitle">
            <span>1.</span>
            {{ t('LP Pixel details') }}
          </div>
        </div>
        <div class="p-16 campaign-details__wrap">
          <div>
            <ui-radio-group v-model="protocol" class="w50 mb-8" :label="t('Protocol')">
              <ui-radio label="http" value="http" :disabled="store.user.userAgentClientHintsEnabled"></ui-radio>
              <ui-radio label="https" value="https"></ui-radio>
            </ui-radio-group>
          </div>

          <ui-select
            v-model="trackingDomainId"
            :label="t('Tracking domain')"
            :items="trackingDomains"
            item-text="domain"
            item-value="id"
            :return-object="false"
            :hide-details="2"
            attach
          ></ui-select>

          <div>
            <lp-pixel-treeview
              :flow="flow"
              v-model:selected-landing="selectedLanding"
            ></lp-pixel-treeview>
          </div>
        </div>
      </div>
      <div class="details lp-pixel-dialog__generated-code-panel">
        <div class="campaign-details__title lp-pixel-dialog__generated-code-panel-header">
          <div class="subtitle">
            <span>2.</span>
            {{ t('Generated code') }}
          </div>
        </div>
        <div class="p-16 campaign-details__wrap lp-pixel-dialog__generated-code-panel-wrap">
          <template v-if="selectedLanding">
            <div>
              <ui-copy-input
                v-for="offerNumber in landingOffers"
                :key="offerNumber"
                :labelHelp="t('Paste this code to the CTA button on the landing page. Replace ‘Click me!’ part with your call-to-action naming, e.g Register now, Sign up, Download etc')"
                :label="t('CTA URL to offer # {0}', [offerNumber])"
                :value="getCtaHref(offerNumber)"
              ></ui-copy-input>
            </div>

            <div class="flow-textarea">
              <ui-copy-textarea
                :label="t('Meta Tag')"
                :labelHelp="t('Paste this tag in the <&hairsp;head> of your landing page for the  correct tracking of OS, browser and device data.')"
                rows="6"
                :value="metaTagContent"
                readonly
                :hide-details="2"
              ></ui-copy-textarea>
            </div>

            <div class="flow-textarea" key="lpPixel">
              <ui-copy-textarea
                :label="t('Landing Page Pixel(For No-Redirect Tracking)')"
                :labelHelp="t('Paste this script along with meta tag in the <&hairsp;head> of your landing page for the correct tracking of OS, browser and device data.')"
                rows="20"
                :value="lpPixel"
                readonly
                :hide-details="2"
              ></ui-copy-textarea>
            </div>
          </template>
          <div v-else key="placeholder" class="lp-pixel-dialog__generated-code-placeholder">
            <div class="lp-pixel-dialog__generated-code-placeholder-icon-wrapper">
              <ui-icon size="32" :color="$colors.gray50" icon="fas fa-exclamation-circle"></ui-icon>
            </div>
            <div class="lp-pixel-dialog__generated-code-placeholder-text">
              {{ t('Fill out all the required fields above and a URL will be automatically generated for you here.') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <section class="footer xs12">
        <ui-button
          :text="t('Close')"
          icon="fas fa-times"
          @click="close"
        ></ui-button>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import LpPixelTreeview from './lp-pixel-treeview.vue';
import { useLpPixelForm, type LpPixelDialogData } from './useLpPixelForm';

const $colors = useColors();

const props = withDefaults(
  defineProps<{
    data?: LpPixelDialogData;
  }>(),
  {
    data: () => ({}),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const {
  t,
  locale,
  store,
  show,
  flow,
  protocol,
  trackingDomainId,
  selectedLanding,
  trackingDomains,
  landingOffers,
  lpPixel,
  metaTagContent,
  getCtaHref,
  close,
} = useLpPixelForm(() => props.data, () => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.copy-btn {
  :deep(.ui-button) {
    width: 100%;
    justify-content: center;
  }
}

.footer {
  display: flex;
  justify-content: flex-end;
}
</style>

<style lang="scss">
.lp-pixel-dialog {
  &__details-panel,
  &__generated-code-panel {
    flex: 1 1 100%;
    min-width: 0;

    @media (min-width: 960px) {
      .lp-pixel-dialog.dialog-wrapper & {
        flex-grow: 0;
      }
    }
  }

  &__details-panel {
    @media (min-width: 960px) {
      .lp-pixel-dialog.dialog-wrapper & {
        flex-basis: 33.333333%;
        max-width: 33.333333%;
      }
    }
  }

  &__generated-code-panel {
    display: flex;
    flex-direction: column;

    @media (min-width: 960px) {
      .lp-pixel-dialog.dialog-wrapper & {
        flex-basis: 66.666667%;
        max-width: 66.666667%;
      }
    }
  }

  &__lp-pixel-script textarea {
    font-size: 13px;
  }

  &__generated-code-panel-header {
    flex-grow: 0;
  }

  &__generated-code-panel-wrap {
    flex-grow: 1;
  }

  &__generated-code-placeholder {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    height: 100%;
  }

  &__generated-code-placeholder-icon-wrapper {
    text-align: center;
  }

  &__generated-code-placeholder-text {
    font-size: 16px;
    text-align: center;
    color: var(--color-gray40);
  }
}
</style>
